import {
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
} from '@mui/material';
import { forwardRef } from 'react';

import { useColorMode } from '../theme/ColorMode.js';
import { elevation, mdSysColor, radius, typography } from '../theme/tokens.js';
import { Icon } from './Icon.js';

/**
 * FSE Menu — MD3 dropdown / popup menu.
 *
 * Spec: `Documentation/Claude design files/preview/menu.html`. MD3 popup:
 * surfaceContainer background, extra-small (4dp) corner, level-3 elevation,
 * 48dp items rendering Body Large text with an optional leading Material
 * Symbols icon. `destructive` items paint in the error role.
 *
 * Controlled by the parent via `anchorEl`/`open`/`onClose` (pass the element
 * the menu should anchor to, e.g. the trigger button ref's `currentTarget`).
 *
 * Wraps MUI `Menu`/`MenuItem`. Owns the public prop surface — consumers never
 * touch MUI's `sx`. Exposed through @fse/ui so consumers never import
 * @mui/material directly (swap-promise).
 */
export interface MenuOption {
  label: string;
  /** Material Symbols ligature name (e.g. "settings"). */
  icon?: string;
  onClick: () => void;
  /** Paint the item in the error role (e.g. "Log out", "Delete"). */
  destructive?: boolean;
}

export interface MenuProps {
  /** Element the menu anchors to; `null` while closed. */
  anchorEl: HTMLElement | null;
  /** Whether the menu is shown. */
  open: boolean;
  /** Fired on backdrop click / Escape / after an item is chosen. */
  onClose: () => void;
  items: MenuOption[];
}

export const Menu = forwardRef<HTMLDivElement, MenuProps>(function Menu(
  { anchorEl, open, onClose, items },
  ref,
) {
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  return (
    <MuiMenu
      ref={ref}
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            minWidth: 240,
            mt: 0.5,
            borderRadius: `${radius.sm}px`,
            backgroundColor: sys.surfaceContainer,
            color: sys.onSurface,
            boxShadow: elevation.level3,
          },
        },
        list: { sx: { py: 1 } },
      }}
    >
      {items.map((item, index) => (
        <MuiMenuItem
          // Labels are not guaranteed unique; index keeps keys stable.
          key={`${item.label}-${index}`}
          onClick={() => {
            item.onClick();
            onClose();
          }}
          sx={{
            height: 48,
            px: 2,
            gap: 1.5,
            fontFamily: typography.fontFamily,
            fontSize: `${typography.bodyMedium.fontSize}px`,
            color: item.destructive ? sys.error : sys.onSurface,
          }}
        >
          {item.icon ? (
            <Icon name={item.icon} size={20} color="inherit" />
          ) : null}
          {item.label}
        </MuiMenuItem>
      ))}
    </MuiMenu>
  );
});
